import React               from 'react';
import View                from '@widesk-ui/components/View';
import useTheme            from '@widesk-ui/hooks/useTheme';
import useThemeMode        from '@widesk-ui/hooks/useThemeMode';
import appStore            from '@/stores/appStore';
import ButtonActions       from '@widesk-ui/components/ButtonActions';
import useAccount          from '@/hooks/useAccount';
import AccountSelector     from './AccountWall/AccountSelector';
import authStore           from '@widesk-core/stores/authStore';
import Tag                 from '@widesk-ui/components/Tag';
import Avatar              from '@widesk-ui/Avatar';
import { UserOutlined }    from '@ant-design/icons';
import { navigate }        from '@widesk-ui/hooks/useNavigate';
import { BellOutlined }    from '@ant-design/icons';
import { LogoutOutlined }  from '@ant-design/icons';
import Button              from '@widesk-ui/components/Button';
import { useAuth }         from '@widesk-ui/hooks/useAuth';
import SunIcon              from '@widesk-ui/components/SunIcon';
import MoonIcon             from '@widesk-ui/components/MoonIcon';
import Logo                 from '@/components/Logo';
import useIsMobile          from '@/hooks/useIsMobile';
// Menu « Tâches » masqué temporairement.
// import MercureEventsMenu    from '@/components/MercureEventsMenu';

// `ButtonActions` sépare icône et libellé par un simple espace : on ajoute une marge sur
// l'icône pour un espacement plus net dans ce menu.
const menuIcon = (icon: React.ReactNode) => <span style={{ marginRight: 6 }}>{icon}</span>;

export default function LayoutHeader() {
	const { account } = useAccount();
	const { colorPrimary, colorBorderSecondary, colorBgContainer, colorTextLightSolid } = useTheme();
	const { mode, setMode } = useThemeMode();
	const isDark = mode === 'dark';
	const isMobile = useIsMobile();

	const badgeBackgroundColor = isDark
		? colorPrimary
		: `color-mix(in srgb, ${colorTextLightSolid} 20%, transparent)`;

	return (
		<View row spread style={{ height: 'calc(100% - 1px)' }}>
			<View row centerV gap={10} paddingL={account ? undefined : 'lg'} style={{ minWidth: 0, overflow: 'hidden', paddingLeft: isMobile ? 56 : undefined }}>
				{account ? <AccountSelector /> : <Logo forceMode="dark" style={{ height: 24 }} />}
			</View>

			<View row heightF style={{ flexShrink: 0, '--nc-trigger-counter-bg': badgeBackgroundColor } as React.CSSProperties}>
				{/* <MercureEventsMenu separatorColor={isDark ? colorBorderSecondary : colorBgContainer} /> */}

				<View
					heightF
					bg={isDark ? undefined : colorPrimary}
					center
					separatorL
					separatorR
					separatorProps={{ bg: isDark ? colorBorderSecondary : colorBgContainer }}
					style={{ overflow: 'hidden' }}
				>
					<Button
						className="nc-trigger"
						aria-label="Notifications"
						style={{ height: '100%' }}
						type="text"
					>
						<View row gap={5}><BellOutlined />{!isMobile && ' Notifications'}</View>
					</Button>
				</View>

				<View
					heightF
					bg={isDark ? undefined : colorPrimary}
					color={isDark ? undefined : 'white'}
					center
					separatorR
					separatorProps={{ bg: isDark ? colorBorderSecondary : colorBgContainer }}
					style={{ overflow: 'hidden' }}
				>
					<Button
						style={{ height: '100%', width: 56, fontSize: 20, color: 'inherit' }}
						type="text"
						onClick={() => setMode(isDark ? 'light' : 'dark')}
						title={isDark ? 'Afficher le thème clair' : 'Afficher le thème sombre'}
					>
						{isDark ? <SunIcon /> : <MoonIcon />}
					</Button>
				</View>

				<UserHeaderSettings avatarBackgroundColor={badgeBackgroundColor} compact={isMobile} />
			</View>
		</View>
	);
}

const UserHeaderSettings = ({ avatarBackgroundColor, compact }: { avatarBackgroundColor: string; compact: boolean }) => {
	const { mode } = useThemeMode();
	const { colorPrimary, colorTextLightSolid } = useTheme();
	const { logout } = useAuth();
	const isDark = mode === 'dark';
	const { firstname } = appStore.user;
	const capitalizedFirstname = firstname.charAt(0).toUpperCase() + firstname.toLocaleLowerCase().slice(1);

	return (
		<View
			className="header-user-settings"
			heightF
			bg={isDark ? undefined : colorPrimary}
			color="white"
			center
			minWidth={compact ? undefined : 150}
			style={{ overflow: 'hidden' }}
		>
			<ButtonActions
				actions={[
					{
						label: 'Informations du compte',
						icon: menuIcon(<UserOutlined />),
						onClick: () => navigate('/my-account'),
					},
					{
						label: 'Déconnexion',
						icon: menuIcon(<LogoutOutlined />),
						danger: true,
						confirm: 'Voulez-vous vraiment vous déconnecter ?',
						onClick: () => logout(),
					},
				]}
				icon={
					authStore.tokenData['impersonate.urn'] ? (
						<Tag icon={<UserOutlined />} color="blue">Vous êtes connecté en tant que {appStore.user.renderName()}</Tag>
					) : (
						<View row centerV gap={8}>
							<Avatar
								size={32}
								style={{
									backgroundColor: avatarBackgroundColor,
									color: colorTextLightSolid,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									flexShrink: 0,
									boxShadow: '0 1px 2px rgba(0, 0, 0, 0.25)',
									fontSize: 14,
									fontWeight: 600,
								}}
							>
								{firstname.charAt(0).toUpperCase()}
							</Avatar>
							{!compact && capitalizedFirstname}
						</View>
					)
				}
				type="text"
				block
				style={{ height: '64px', borderRadius: 0 }}
			/>
		</View>
	);
};
